import { useEffect, useState } from "react";
import { selectedPresaleSelector, topicsSelector } from "Redux/selectors";
import { fetchInspirationTopics } from "Redux/Reducers/inspirations";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { InspirationItem, UserPublishedInspiration } from "Model/Inspiration";
import { useFormik } from "formik";
import { useSession } from "Context/userAuthContext";
import {
  publishInspirationItem,
  checkInspirationPublishStatus,
  unpublishInspirationItem,
} from "Util/firebaseHelper";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { InfoBox } from "Components/AllComponents";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

type Props = {
  open: boolean;
  handleClose: () => void;
};
interface SupplierFormData {
  cutterSellers: string;
  packagingSellers: string;
  otherSuppliers: string;
}

export const ShareToInspo = ({ open, handleClose }: Props) => {
  const { user } = useSession();
  const selectedPresale = useAppSelector(selectedPresaleSelector);
  const inspirationTopics = useAppSelector(topicsSelector);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [checkedItems, setCheckedItems] = useState<{
    [key: number]: SupplierFormData;
  }>({});
  const [publishingStatus, setPublishingStatus] = useState<
    "idle" | "publishing" | "completed"
  >("idle");
  const [publishStatus, setPublishStatus] = useState<{
    [key: string]: UserPublishedInspiration;
  }>({});

  // Load publish status and initialize checked items when dialog opens
  useEffect(() => {
    const loadPublishStatus = async () => {
      if (!open || !user || !selectedPresale.presaleId) return;
      const status = await checkInspirationPublishStatus(
        user.uid,
        selectedPresale.presaleId
      );
      setPublishStatus(status);

      // Check if any items are published
      const hasPublishedItems = Object.values(status).some(
        (s) => s.status === "published"
      );
      console.log("status map", status);
      // Initialize checked items
      const initialChecked: { [key: number]: SupplierFormData } = {};
      selectedPresale.presaleItems?.forEach((item, index) => {
        if (!hasPublishedItems || status[item.id || ""]) {
          // Check all items if none are published, or check published items
          initialChecked[index] = {
            cutterSellers:
              status[item.id || ""]?.originalData.suppliers.cutterSellers?.join(
                ", "
              ) || "",
            packagingSellers:
              status[
                item.id || ""
              ]?.originalData.suppliers.packagingSellers?.join(", ") || "",
            otherSuppliers:
              status[
                item.id || ""
              ]?.originalData.suppliers.otherSuppliers?.join(", ") || "",
          };
        }
      });
      setCheckedItems(initialChecked);
    };
    loadPublishStatus();
  }, [open, user, selectedPresale.presaleId, selectedPresale.presaleItems]);

  const handleToggle = (index: number, presaleItem: any) => () => {
    setCheckedItems((prev) => {
      const newState = { ...prev };

      if (newState[index]) {
        delete newState[index];
      } else {
        newState[index] = {
          cutterSellers: prev[index]?.cutterSellers || "",
          packagingSellers: prev[index]?.packagingSellers || "",
          otherSuppliers: prev[index]?.otherSuppliers || "",
        };
      }
      return newState;
    });
  };

  const [selectedTopic, setSelectedTopic] = useState("");

  useEffect(() => {
    // Only set topic if there are already published items
    const publishedItemTopicId = Object.values(publishStatus).find(
      (item) => item?.status === "published"
    )?.topicId;

    if (publishedItemTopicId) {
      // Find topic name from ID
      const topic = inspirationTopics.find(
        (t) => t.id === publishedItemTopicId
      );
      if (topic) {
        setSelectedTopic(topic.name);
      }
    } else {
      // No published items - leave selection empty
      setSelectedTopic("");
    }
  }, [inspirationTopics, publishStatus]);

  const handleTopicChange = (event: { target: { value: string } }) => {
    setSelectedTopic(event.target.value);
  };

  const handleSupplierChange = (
    index: number,
    field: keyof SupplierFormData,
    value: string
  ) => {
    setCheckedItems((prev) => {
      const currentItem = prev[index];
      if (!currentItem) return prev;

      return {
        ...prev,
        [index]: {
          ...currentItem,
          [field]: value,
        },
      };
    });
  };
  const dispatch = useAppDispatch();

  const handleUnpublishItem = (index: number, topicId: string) => {
    const presaleItem = selectedPresale.presaleItems[index];
    if (!user || !presaleItem) return null;

    return unpublishInspirationItem(user.uid, {
      id: presaleItem.id || "",
      itemId: presaleItem.id || "",
      topicId: topicId,
      publishedAt: "",
      status: "unpublished",
      originalData: {} as InspirationItem,
    });
  };

  const handlePublishItem = (
    index: number,
    data: SupplierFormData,
    topicId: string
  ) => {
    const presaleItem = selectedPresale.presaleItems[index];
    if (!user || !presaleItem) return null;

    const publishedItem = publishStatus[presaleItem.id || ""];
    const currentTopicId = publishedItem?.topicId;

    const item: InspirationItem = {
      id: presaleItem.id || "",
      img: presaleItem.img || "",
      href: presaleItem.img || "",
      description: presaleItem.title || "",
      price: presaleItem.price || 0,
      title: presaleItem.title || "",
      link: `/presale/${selectedPresale.presaleId}`,
      presaleId: selectedPresale.presaleId,
      suppliers: {
        cutterSellers: data.cutterSellers
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s),
        packagingSellers: data.packagingSellers
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s),
        otherSuppliers: data.otherSuppliers
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s),
      },
      publishedBy: user.uid,
      publishedAt: new Date().toISOString(),
    };

    // If topic has changed, pass both topicIds to handle the migration
    return publishInspirationItem(
      user.uid,
      topicId,
      item,
      currentTopicId !== topicId ? topicId : undefined
    );
  };

  const handlePublish = async () => {
    if (!user) return;

    setPublishingStatus("publishing");
    const selectedTopicData = inspirationTopics.find(
      (t) => t.name === selectedTopic
    );
    if (!selectedTopicData) return;

    try {
      const publishPromises: Promise<string>[] = [];
      const unpublishPromises: Promise<void>[] = [];

      // Find published items that are not in checkedItems (to be deleted)
      // Only process items that have images
      selectedPresale.presaleItems
        ?.map((item, index) => ({ item, index }))
        .filter(({ item }) => item.img)
        .forEach(({ item, index }) => {
          const isPublished =
            publishStatus[item.id || ""]?.status === "published";
          const itemData = checkedItems[index];

          if (isPublished) {
            // For published items
            if (!itemData) {
              // Unchecked published item - delete it
              const promise = handleUnpublishItem(index, selectedTopicData.id);
              if (promise) unpublishPromises.push(promise);
            }
            // If checked with action="keep", do nothing
            if (itemData) {
              // Update supplier info for published items
              const promise = handlePublishItem(
                index,
                itemData,
                selectedTopicData.id
              );
              if (promise) publishPromises.push(promise);
            }
          } else {
            // For unpublished items
            if (itemData) {
              // New item marked for publishing
              const promise = handlePublishItem(
                index,
                itemData,
                selectedTopicData.id
              );
              if (promise) publishPromises.push(promise);
            }
          }
        });

      await Promise.all([...publishPromises, ...unpublishPromises]);

      // Refresh publish status and inspiration data
      const newStatus = await checkInspirationPublishStatus(
        user.uid,
        selectedPresale.presaleId
      );
      setPublishStatus(newStatus);
      // Wait for the inspiration topics to be fetched and updated
      await dispatch(fetchInspirationTopics());

      setCheckedItems({}); // Clear selections after successful update
      setPublishingStatus("completed");
      handleClose();
    } catch (error: any) {
      console.error("Error publishing/unpublishing items:", error);
      // If it's a permission error or missing document error during unpublish,
      // still proceed with UI updates since the document might be already deleted
      if (error.code === "permission-denied" || error.code === "not-found") {
        await dispatch(fetchInspirationTopics());
        setCheckedItems({});
        setPublishingStatus("completed");
        handleClose();
      } else {
        setPublishingStatus("idle");
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <AutoAwesomeRoundedIcon sx={{ mr: 1 }} />
        Share your art!
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          Help the community grow! Would you like to add your designs to the
          Inspiration Board to inspire fellow cookiers?
        </Typography>
        <Select
          value={selectedTopic}
          onChange={handleTopicChange}
          displayEmpty
          sx={{
            "& .MuiInputBase-input": {
              padding: 1,
              fontWeight: 500,
            },
          }}
        >
          <MenuItem value="" disabled>
            Presale theme
          </MenuItem>
          {inspirationTopics.map((topic) => (
            <MenuItem key={topic.id} value={topic.name}>
              {topic.name}
            </MenuItem>
          ))}
        </Select>
        <List disablePadding sx={{ display: selectedTopic ? "auto" : "none" }}>
          {selectedPresale.presaleItems
            ?.map((item, originalIndex) => ({ item, originalIndex }))
            .filter(({ item }) => item.img)
            .map(({ item: presaleItem, originalIndex }, displayIndex) => (
              <Stack key={originalIndex} sx={{ mt: 1 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={handleToggle(originalIndex, presaleItem)}
                    dense
                    disabled={
                      publishingStatus === "publishing" || !selectedTopic
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={!!checkedItems[originalIndex]}
                      tabIndex={-1}
                      disableRipple
                      sx={{ mr: 1 }}
                    />
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      flex={1}
                    >
                      <Box
                        sx={{
                          width: "64px",
                          height: "64px",
                          borderRadius: 1,
                          backgroundColor: "background.default",
                        }}
                      >
                        {presaleItem.img ? (
                          <img
                            src={presaleItem.img}
                            style={{
                              width: "64px",
                              height: "64px",
                              display: "block",
                              borderRadius: "4px",
                              objectFit: "cover",
                            }}
                            alt={presaleItem.title}
                          />
                        ) : null}
                      </Box>
                      <ListItemText
                        id={presaleItem.title}
                        primary={presaleItem.title}
                      />
                      {publishStatus[presaleItem.id || ""]?.status ===
                        "published" && (
                        <Chip
                          size="small"
                          icon={<PublicRoundedIcon />}
                          label="Published"
                        />
                      )}
                    </Stack>
                  </ListItemButton>
                </ListItem>
                {checkedItems[originalIndex] ? (
                  <InfoBox>
                    <TextField
                      fullWidth
                      multiline
                      margin="dense"
                      size="small"
                      label="Cutter sources"
                      value={checkedItems[originalIndex].cutterSellers}
                      onChange={(e) =>
                        handleSupplierChange(
                          originalIndex,
                          "cutterSellers",
                          e.target.value
                        )
                      }
                    />
                    <TextField
                      fullWidth
                      multiline
                      margin="dense"
                      size="small"
                      label="Packaging sources"
                      value={checkedItems[originalIndex].packagingSellers}
                      onChange={(e) =>
                        handleSupplierChange(
                          originalIndex,
                          "packagingSellers",
                          e.target.value
                        )
                      }
                    />
                    <TextField
                      fullWidth
                      multiline
                      margin="dense"
                      size="small"
                      label="Other suppliers"
                      value={checkedItems[originalIndex].otherSuppliers}
                      onChange={(e) =>
                        handleSupplierChange(
                          originalIndex,
                          "otherSuppliers",
                          e.target.value
                        )
                      }
                    />
                  </InfoBox>
                ) : null}
              </Stack>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handlePublish}
          variant="contained"
          disabled={publishingStatus === "publishing" || !selectedTopic}
        >
          {publishingStatus === "publishing"
            ? "Saving..."
            : Object.values(publishStatus).some(
                  (status) => status?.status === "published"
                )
              ? "Save changes"
              : "Add to board"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
